import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import moment from "moment";
import { getBookingChartData } from "../../../../actions/admin/booking";
import { MultiSelectField } from "../../_components/AdminFilterBar";

/**
 * Monthly booking volume for the admin dashboard.
 *
 * Two series — confirmed and cancelled — grouped per month over a rolling
 * 12-month window, filterable by payment status and account type. Both filters
 * start empty, which the API reads as "no filter", so the default view is
 * everything.
 *
 * Drawn as inline SVG rather than pulling in a charting library: two series over
 * twelve bands needs bars, a baseline and a hover tooltip, and nothing else.
 */

// Validated against a #FFFFFF surface: all-pairs CVD separation ΔE 17.0
// (protan) and 33.2 for normal vision, both well clear of the floors.
const SERIES = [
  { key: "confirmed", label: "Confirmed", color: "#20639B" },
  { key: "cancelled", label: "Cancelled", color: "#EA4335" },
];

const ACCOUNT_OPTIONS = [
  { value: "business", label: "Business" },
  { value: "individual", label: "Individual" },
  { value: "guest", label: "Guest" },
];

const MONTHS_IN_WINDOW = 12;

const CHART_HEIGHT = 260;
const AXIS_GUTTER = 46; // room for the y-axis tick labels
const PLOT_PADDING_RIGHT = 8;
const PLOT_PADDING_TOP = 12;
const X_LABEL_BAND = 38;
const MAX_BAR_WIDTH = 24;
const BAR_GAP = 2; // the surface gap between the two bars of a group
const BAR_RADIUS = 4;

const humanizeStatus = (value) =>
  value === "NONE"
    ? "No payment status"
    : value
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

// Round the axis maximum up to a clean number so the ticks read 0 / 10 / 20 / 30.
const niceCeiling = (value, tickCount) => {
  if (value <= 0) return tickCount;
  const rough = value / tickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) || magnitude * 10;
  return step * tickCount;
};

// A bar with rounded data-end corners and square corners on the baseline.
const barPath = (x, y, width, height) => {
  if (height <= 0) return "";
  const r = Math.min(BAR_RADIUS, width / 2, height);
  return [
    `M${x},${y + height}`,
    `V${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `H${x + width - r}`,
    `Q${x + width},${y} ${x + width},${y + r}`,
    `V${y + height}`,
    "Z",
  ].join(" ");
};

const useMeasuredWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const update = () => setWidth(node.clientWidth);
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
};

const Legend = () => (
  <div className="tw:flex tw:items-center tw:gap-4">
    {SERIES.map((series) => (
      <div key={series.key} className="tw:flex tw:items-center tw:gap-[6px]">
        <span
          className="tw:block tw:h-[10px] tw:w-[10px] tw:rounded-[2px]"
          style={{ backgroundColor: series.color }}
        />
        <span className="tw:text-[13px] tw:leading-[18px] tw:!text-[#55555a]">{series.label}</span>
      </div>
    ))}
  </div>
);

const MonthlyBookingsChart = () => {
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [accountType, setAccountType] = useState([]);
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [containerRef, width] = useMeasuredWidth();

  const fetchChart = useCallback(async () => {
    setLoading(true);

    const params = new URLSearchParams({
      startMonthYear: moment().subtract(MONTHS_IN_WINDOW - 1, "months").format("MM/YYYY"),
      toMonthYear: moment().format("MM/YYYY"),
    });

    if (paymentStatus.length) {
      params.set("paymentStatus", paymentStatus.map((item) => item.value).join(","));
    }
    if (accountType.length) {
      params.set("accountType", accountType.map((item) => item.value).join(","));
    }

    const response = await getBookingChartData("bar-booking-monthly", `?${params.toString()}`);

    setRows(response?.chartData || []);
    // The options list is built before the filters are applied, so it stays
    // stable as the admin narrows things down.
    if (response?.paymentStatusOptions) {
      setPaymentStatusOptions(
        response.paymentStatusOptions.map((value) => ({ value, label: humanizeStatus(value) })),
      );
    }
    setLoading(false);
  }, [paymentStatus, accountType]);

  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  const plotWidth = Math.max(width - AXIS_GUTTER - PLOT_PADDING_RIGHT, 0);
  const bandWidth = rows.length ? plotWidth / rows.length : 0;
  const barWidth = Math.min(MAX_BAR_WIDTH, Math.max((bandWidth - 16 - BAR_GAP) / 2, 2));
  const groupWidth = barWidth * 2 + BAR_GAP;

  const tickCount = 4;
  const peak = rows.reduce((max, row) => Math.max(max, row.confirmed, row.cancelled), 0);
  const axisMax = niceCeiling(peak, tickCount);
  const plotHeight = CHART_HEIGHT - PLOT_PADDING_TOP - X_LABEL_BAND;
  const yFor = (value) => PLOT_PADDING_TOP + plotHeight - (value / axisMax) * plotHeight;

  const hasData = rows.some((row) => row.confirmed > 0 || row.cancelled > 0);

  return (
    <section className="tw:mt-[34px] tw:rounded-[5px] tw:border tw:border-[#E6E6E6] tw:bg-white tw:p-[24px]">
      <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-3">
        <div>
          {/* `!` throughout: the un-layered admin CSS styles bare h2/p elements. */}
          <h2 className="tw:mb-1 tw:!text-[18px] tw:!font-semibold tw:!leading-[normal] tw:!text-[#212529]">
            Monthly bookings
          </h2>
          <p className="tw:mb-0 tw:!text-[13px] tw:!leading-[18px] tw:!text-[#7C7B7B]">
            Confirmed and cancelled bookings over the last {MONTHS_IN_WINDOW} months, by the date the
            booking was made.
          </p>
        </div>
        <Legend />
      </div>

      <div className="tw:mt-[18px] tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-4">
        <MultiSelectField
          label="Payment Status"
          value={paymentStatus}
          onChange={(value) => setPaymentStatus(value || [])}
          options={paymentStatusOptions}
          placeholder="All"
        />
        <MultiSelectField
          label="Account"
          value={accountType}
          onChange={(value) => setAccountType(value || [])}
          options={ACCOUNT_OPTIONS}
          placeholder="All"
        />
      </div>

      <div className="tw:relative tw:mt-[20px]" ref={containerRef}>
        {loading ? (
          <div
            className="tw:w-full tw:animate-pulse tw:rounded-[5px] tw:bg-[#F3F3F3]"
            style={{ height: CHART_HEIGHT }}
          />
        ) : !hasData ? (
          <div
            className="tw:flex tw:w-full tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[#FAFAFA] tw:text-[15px] tw:!text-[#7C7B7B]"
            style={{ height: CHART_HEIGHT }}
          >
            No bookings in this period.
          </div>
        ) : width > 0 ? (
          <svg
            width={width}
            height={CHART_HEIGHT}
            role="img"
            aria-label={`Monthly bookings for the last ${MONTHS_IN_WINDOW} months, confirmed versus cancelled`}
          >
            {Array.from({ length: tickCount + 1 }).map((_, index) => {
              const value = (axisMax / tickCount) * index;
              const y = yFor(value);

              return (
                <g key={value}>
                  <line
                    x1={AXIS_GUTTER}
                    x2={width - PLOT_PADDING_RIGHT}
                    y1={y}
                    y2={y}
                    stroke="#ECECEC"
                    strokeWidth="1"
                  />
                  <text
                    x={AXIS_GUTTER - 10}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill="#9A9A9A"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {value.toLocaleString()}
                  </text>
                </g>
              );
            })}

            {rows.map((row, index) => {
              const bandStart = AXIS_GUTTER + bandWidth * index;
              const groupStart = bandStart + (bandWidth - groupWidth) / 2;
              const [month, year] = row.name.split(" ");
              const showYear = index === 0 || rows[index - 1].name.split(" ")[1] !== year;

              return (
                <g key={row.key}>
                  {SERIES.map((series, seriesIndex) => {
                    const value = row[series.key];
                    const barHeight = (value / axisMax) * plotHeight;
                    const x = groupStart + seriesIndex * (barWidth + BAR_GAP);
                    const isHovered = hovered?.index === index && hovered?.series === series.key;

                    return (
                      <g key={series.key}>
                        {/* A full-height hit area so short bars stay easy to hover. */}
                        <rect
                          x={x}
                          y={PLOT_PADDING_TOP}
                          width={barWidth}
                          height={plotHeight}
                          fill="transparent"
                          onMouseEnter={() =>
                            setHovered({ index, series: series.key, x: x + barWidth / 2 })
                          }
                          onMouseLeave={() => setHovered(null)}
                        />
                        {barHeight > 0 ? (
                          <path
                            d={barPath(x, yFor(value), barWidth, barHeight)}
                            fill={series.color}
                            opacity={hovered && !isHovered ? 0.55 : 1}
                            pointerEvents="none"
                          />
                        ) : null}
                      </g>
                    );
                  })}

                  <text
                    x={bandStart + bandWidth / 2}
                    y={CHART_HEIGHT - X_LABEL_BAND + 22}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#7C7B7B"
                  >
                    {month}
                  </text>
                  {showYear ? (
                    <text
                      x={bandStart + bandWidth / 2}
                      y={CHART_HEIGHT - X_LABEL_BAND + 36}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#B5B5B5"
                    >
                      {year}
                    </text>
                  ) : null}
                </g>
              );
            })}

            <line
              x1={AXIS_GUTTER}
              x2={width - PLOT_PADDING_RIGHT}
              y1={yFor(0)}
              y2={yFor(0)}
              stroke="#D9D9D9"
              strokeWidth="1"
            />
          </svg>
        ) : null}

        {hovered && rows[hovered.index] ? (
          <div
            className="tw:pointer-events-none tw:absolute tw:z-20 tw:-translate-x-1/2 tw:rounded-[5px] tw:bg-[#282828] tw:px-3 tw:py-2 tw:text-[12px] tw:leading-[18px] tw:!text-white tw:whitespace-nowrap"
            style={{ left: hovered.x, top: 0 }}
          >
            <div className="tw:font-semibold">{rows[hovered.index].name}</div>
            {SERIES.map((series) => (
              <div key={series.key} className="tw:flex tw:items-center tw:gap-[6px]">
                <span
                  className="tw:block tw:h-[8px] tw:w-[8px] tw:rounded-[2px]"
                  style={{ backgroundColor: series.color }}
                />
                <span>
                  {series.label}: {rows[hovered.index][series.key].toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Same numbers as the chart, for screen readers and anyone who wants the values. */}
      <table className="visually-hidden">
        <caption>Monthly bookings, confirmed and cancelled</caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            {SERIES.map((series) => (
              <th key={series.key} scope="col">
                {series.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.name}</th>
              {SERIES.map((series) => (
                <td key={series.key}>{row[series.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

export default MonthlyBookingsChart;
